// src/components/PropertyList/HorneroChat.jsx
//
// Panel lateral de chat con Hornero — el agente IA que ayuda al user a
// redactar textos para sus páginas adicionales del informe. El user le pide
// "generá una intro" o "redactá un cierre" y el agente devuelve texto
// formateado (markdown simple) que el user puede copiar para diseñar su
// página externamente y subirla desde el dashboard.
//
// Backend esperado: POST <API_URL>/acm/generate-text
//   body: { prompt: string, context?: { wizardData, properties } }
//   response: { text: string }
//
// Si el endpoint no existe (404) o falla, el chat muestra un mensaje claro
// y NO crashea el editor. El user puede copiar el prompt manualmente.

import React, { useState, useRef, useEffect } from "react";
import horneroAvatar from "../../assets/hornero-avatar.png";

const apiUrl = process.env.REACT_APP_API_URL;

async function horneroRequest(path, payload) {
  const token=localStorage.getItem("auth_token") || localStorage.getItem("token");
  const response=await fetch(`${apiUrl}/acm${path}`,{
    method:payload === undefined ? "GET" : "POST",
    headers:{"Content-Type":"application/json",...(token?{Authorization:`Bearer ${token}`}:{})},
    ...(payload === undefined ? {} : {body:JSON.stringify(payload)}),
  });
  const data=await response.json();
  if(!response.ok){const error=new Error(typeof data.detail === "string" ? data.detail : "No pude completar la consulta.");error.conversationId=data.conversation_id;throw error;}
  return data;
}
const GREETING={role:"assistant",text:"¡Hola! Soy Hornero. Te ayudo con los textos y el análisis de tu informe. Podemos retomar una conversación o empezar una nueva."};


// Prompts rápidos — el user clickea uno y se manda como mensaje inicial.
// Pensados para los tipos de página adicional más frecuentes.
const QUICK_PROMPTS = [
  {
    label: "Intro de la tasación",
    prompt:
      "Generá una introducción profesional para esta tasación comparativa de mercado. Mencioná que es un análisis basado en propiedades comparables de la misma zona. Tono cordial pero técnico. Entre 80-120 palabras.",
  },
  {
    label: "Tabla comparativa",
    prompt:
      "Armá una TABLA markdown comparativa con TODAS las propiedades del informe que te pasé en el contexto. Columnas: Prop, Precio, m² Cubierta, USD/m², Ambientes, Dormitorios. Usá los datos REALES, no inventés. Antes de la tabla, una intro de 1-2 oraciones explicando el ranking (de menor a mayor precio).",
  },
  {
    label: "Resumen del análisis",
    prompt:
      "Redactá un resumen del análisis comparativo destacando: rango de precios, antigüedad de las publicaciones y características clave. Usá los datos REALES de las propiedades del contexto. Entre 100-150 palabras. Usá viñetas cuando ayude.",
  },
  {
    label: "Conclusión + recomendación",
    prompt:
      "Escribí la conclusión del informe con la recomendación de precio. Mencioná que el precio sugerido balancea velocidad de venta y valor real. Cordial, firme. 60-100 palabras.",
  },
  {
    label: "Argumentos para el cliente",
    prompt:
      "Generá 4-5 argumentos cortos para justificar el precio sugerido frente al cliente, usando los comparables que te pasé. Cada uno como bullet point. Foco: comparables reales, tiempos de mercado, demanda.",
  },
];

const HorneroChat = ({
  wizardData,
  properties = [],
  // Si está seteado, el chat muestra "Insertar en esta página" en cada
  // respuesta del assistant → la inserta en la página custom activa.
  onInsertToPage,
  activePageTitle,
}) => {
  const [messages, setMessages] = useState([GREETING]);
  const [conversationId,setConversationId]=useState(null);
  const [conversations,setConversations]=useState([]);
  const [restoring,setRestoring]=useState(true);
  const [notice,setNotice]=useState("");
  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      try {
        const list=await horneroRequest('/conversations');
        if(cancelled)return;
        setConversations(list.conversations);
        if(list.conversations.length){
          const {conversation}=await horneroRequest(`/conversations/${list.conversations[0].id}`);
          if(cancelled)return;
          setConversationId(conversation.id);
          setMessages(conversation.messages.map(m=>({...m,text:m.content})));
        }
      }catch{if(!cancelled)setNotice('No pude recuperar el historial. Podés reintentar abriendo el chat nuevamente.');}
      finally{if(!cancelled)setRestoring(false);}
    })();
    return()=>{cancelled=true;};
  },[]);
  const selectConversation=async(id)=>{
    if(loading||restoring)return;
    if(!id){setConversationId(null);setMessages([GREETING]);setNotice('');return;}
    setRestoring(true);setNotice('');
    try{const {conversation}=await horneroRequest(`/conversations/${id}`);setConversationId(conversation.id);setMessages(conversation.messages.map(m=>({...m,text:m.content})));}
    catch{setNotice('No pude abrir esa conversación.');}finally{setRestoring(false);}
  };
  const feedback=async(id,rating)=>{
    try{await horneroRequest(`/messages/${id}/feedback`,{rating});setMessages(items=>items.map(m=>m.id===id?{...m,feedback:rating}:m));setNotice('');}
    catch{setNotice('No se guardó tu opinión. Probá de nuevo.');}
  };
  const recordEvent=(id,kind)=>{if(id)horneroRequest(`/messages/${id}/events`,{kind}).catch(()=>{});};
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Auto-scroll al fondo cuando llega nuevo mensaje.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (textToSend) => {
    const text = (textToSend ?? input).trim();
    if (!text || loading || restoring) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);

    try {
      const token =
        localStorage.getItem("auth_token") || localStorage.getItem("token");
      const res = await fetch(`${apiUrl}/acm/generate-text`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          prompt: text,
          conversation_id: conversationId,
          context: {
            wizardData,
            propertiesCount: properties?.length || 0,
            // Resumen de cada propiedad — campos clave para que la IA pueda
            // generar tablas/comparativas. Limitamos los campos para no
            // saturar el contexto del modelo.
            properties: (properties || []).map((p, i) => ({
              n: i + 1,
              address: p.address,
              location: p.location,
              price: p.price,
              currency: p.price_currency,
              m2_total: p.square_meters_total,
              m2_covered: p.square_meters_covered,
              rooms: p.rooms,
              bedrooms: p.bedrooms,
              bathrooms: p.bathrooms,
              parking: p.parking,
              has_pool: p.hasPool,
              has_grill: p.hasGrill,
              has_garden: p.hasGarden,
              days_published: p.days_published,
            })),
          },
        }),
      });
      const data = await res.json();
      if(data.conversation_id)setConversationId(data.conversation_id);
      if(!res.ok)throw new Error(typeof data.detail === "string"?data.detail:'No pude completar la respuesta.');
      horneroRequest('/conversations').then(list=>setConversations(list.conversations)).catch(()=>{});
      setMessages((m) => [
        ...m,
        { id:data.message_id, role: "assistant", text: data.text || "(sin respuesta)" },
      ]);
    } catch (err) {
      const friendly = err?.message?.includes("404")
        ? "El backend del chat todavía no está activo. Pedíle al admin que habilite /acm/generate-text."
        : `No pude generar el texto: ${err?.message || "error desconocido"}.`;
      setMessages((m) => [
        ...m,
        { role: "assistant", text: friendly, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      recordEvent(id,"copied");
    } catch {
      // Fallback silencioso
    }
  };

  return (
    <div
      style={{
        width: 340,
        flexShrink: 0,
        borderLeft: "1px solid #E2E8F0",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "100%",
        minHeight: 0,
        alignSelf: "stretch",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "12px 14px",
          borderBottom: "1px solid #E2E8F0",
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "linear-gradient(135deg, #FFF8F4, #fff)",
        }}
      >
        <img
          src={horneroAvatar}
          alt="Hornero"
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            objectFit: "cover",
            background: "#FFF8F4",
            border: "1.5px solid #FA7317",
          }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>
            Hornero
          </div>
          <div style={{ fontSize: 10, color: "#64748B" }}>
            Asistente para textos del informe
          </div>
        </div>
      </div>

      <div style={{padding:"8px 12px",borderBottom:"1px solid #e2e8f0"}}>
        <select aria-label="Conversación con Hornero" value={conversationId || ""} disabled={loading||restoring} onChange={event=>selectConversation(Number(event.target.value)||null)} style={{width:"100%",fontSize:12,padding:6,borderRadius:6,border:"1px solid #e2e8f0"}}>
          <option value="">Nueva conversación</option>
          {conversations.map(c=><option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        {restoring && <small>Recuperando conversación…</small>}
        {notice && <small role="status" style={{display:"block",color:"#9a3412",marginTop:6}}>{notice}</small>}
      </div>
      {/* Mensajes */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              alignSelf: m.role === "user" ? "flex-end" : "flex-start",
              maxWidth: "85%",
              padding: "8px 11px",
              borderRadius: 10,
              background:
                m.role === "user" ? "#FA7317" : m.error ? "#FEF2F2" : "#F1F5F9",
              color:
                m.role === "user" ? "#fff" : m.error ? "#991B1B" : "#0F172A",
              fontSize: 12,
              lineHeight: 1.45,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {m.text}
            {m.role === "assistant" && !m.error && (m.id || i > 0) && (
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  marginTop: 6,
                  flexWrap: "wrap",
                }}
              >
                {onInsertToPage && (
                  <button
                    type="button"
                    onClick={async () => {await onInsertToPage(m.text);recordEvent(m.id,"inserted");}}
                    style={{
                      border: "1px solid #FA7317",
                      background: "#FA7317",
                      color: "#fff",
                      fontSize: 10,
                      padding: "3px 8px",
                      borderRadius: 4,
                      cursor: "pointer",
                      fontWeight: 700,
                    }}
                    title={`Insertar en "${activePageTitle || "esta página"}"`}
                  >
                    <i
                      className="pi pi-arrow-right"
                      style={{ fontSize: 9, marginRight: 4 }}
                    />
                    Insertar en la página
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => copy(m.text,m.id)}
                  style={{
                    border: "1px solid #CBD5E1",
                    background: "#fff",
                    color: "#0F172A",
                    fontSize: 10,
                    padding: "3px 8px",
                    borderRadius: 4,
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                  title="Copiar texto al portapapeles"
                >
                  <i
                    className="pi pi-copy"
                    style={{ fontSize: 9, marginRight: 4 }}
                  />
                  Copiar
                </button>
                {m.id && <>
                  <button type="button" aria-pressed={m.feedback==="useful"} onClick={()=>feedback(m.id,"useful")} style={{fontSize:10,border:"1px solid #cbd5e1",borderRadius:4,padding:"3px 6px",background:m.feedback==="useful"?"#ffedd5":"white"}}>Me sirvió</button>
                  <button type="button" aria-pressed={m.feedback==="not_useful"} onClick={()=>feedback(m.id,"not_useful")} style={{fontSize:10,border:"1px solid #cbd5e1",borderRadius:4,padding:"3px 6px",background:m.feedback==="not_useful"?"#ffedd5":"white"}}>No me sirvió</button>
                </>}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div
            style={{
              alignSelf: "flex-start",
              padding: "8px 11px",
              background: "#F1F5F9",
              borderRadius: 10,
              fontSize: 12,
              color: "#64748B",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: 11 }} />
            Pensando…
          </div>
        )}
      </div>

      {/* Quick prompts */}
      <div
        style={{
          padding: "8px 12px",
          borderTop: "1px solid #E2E8F0",
          background: "#F8FAFC",
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          flexShrink: 0,
          maxHeight: 76,
          overflowY: "auto",
        }}
      >
        {QUICK_PROMPTS.map((qp, i) => (
          <button
            key={i}
            type="button"
            onClick={() => send(qp.prompt)}
            disabled={loading || restoring}
            style={{
              padding: "3px 8px",
              border: "1px solid #CBD5E1",
              borderRadius: 999,
              background: "#fff",
              fontSize: 10,
              color: "#475569",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
          >
            {qp.label}
          </button>
        ))}
      </div>

      {/* Input */}
      <div
        style={{
          padding: 10,
          borderTop: "1px solid #E2E8F0",
          display: "flex",
          gap: 6,
          flexShrink: 0,
        }}
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Pedíle algo a Hornero…"
          rows={2}
          disabled={loading || restoring}
          style={{
            flex: 1,
            padding: "6px 8px",
            border: "1px solid #CBD5E1",
            borderRadius: 6,
            fontSize: 12,
            fontFamily: "inherit",
            resize: "none",
            outline: "none",
          }}
        />
        <button
          type="button"
          onClick={() => send()}
          disabled={loading || restoring || !input.trim()}
          style={{
            background: "#FA7317",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "0 12px",
            cursor: loading || !input.trim() ? "not-allowed" : "pointer",
            opacity: loading || !input.trim() ? 0.5 : 1,
            fontSize: 14,
          }}
          title="Enviar (Enter)"
        >
          <i className="pi pi-send" />
        </button>
      </div>
    </div>
  );
};

export default HorneroChat;
